import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { ThemeClasses } from "../../../types/components";
import type { FormField, RepeatColumn } from "./ApiCrudSection";

// Renderiza el formulario alta/edición desde el esquema de campos de una sección
// y devuelve los valores al enviar. Adaptado de
// Layouts/mantenimiento/components/EntityForm.tsx, añadiendo:
//   • `checkbox` — banderas booleanas (consumo automático, imprimir en receta /
//     en cuaderno, ítem crítico del check list). Se serializa como "true"/"false".
//   • columnas `select` en los campos `repeat` — necesarias para elegir la base
//     de cálculo del consumo (AgroCloud#80) o el tipo de un campo configurable.
// Presentacional + estado local (sin react-hook-form, como el resto del patrón).
interface EntityFormProps {
  fields: FormField[];
  initial?: Record<string, unknown>;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  submitLabel: string;
  onCancel: () => void;
  onSubmit: (values: Record<string, string>) => void;
}

export default function EntityForm({
  fields,
  initial,
  themeClasses,
  isDarkMode,
  submitLabel,
  onCancel,
  onSubmit,
}: EntityFormProps) {
  const { t } = useTranslation("common");
  const [values, setValues] = useState<Record<string, string>>(() => {
    const base: Record<string, string> = {};
    for (const f of fields) {
      if (f.type === "repeat") {
        // Los campos `repeat` llevan un array de filas, serializado como JSON
        // para que el mapa de valores siga siendo Record<string, string>.
        const seed = initial?.[f.key];
        base[f.key] = JSON.stringify(Array.isArray(seed) ? seed : []);
        continue;
      }
      if (f.type === "checkbox") {
        const seed = initial?.[f.key] ?? f.defaultValue ?? false;
        base[f.key] = String(Boolean(seed && seed !== "false"));
        continue;
      }
      const seed = initial?.[f.key] ?? f.defaultValue ?? "";
      base[f.key] = String(seed);
    }
    return base;
  });
  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);

  const inputBase = `w-full rounded-lg border px-3 py-2 text-sm outline-none transition-colors ${
    isDarkMode
      ? "bg-[#232323] border-dark-border text-white focus:border-primary-500"
      : "bg-white border-gray-300 text-gray-900 focus:border-primary-500"
  }`;

  const set = (key: string, v: string) => setValues((prev) => ({ ...prev, [key]: v }));

  const handleSubmit = async () => {
    const nextErrors: Record<string, boolean> = {};
    for (const f of fields) {
      // Un checkbox nunca está "vacío": no se valida como requerido.
      if (f.type === "checkbox") continue;
      if (f.required && !values[f.key]?.trim()) nextErrors[f.key] = true;
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    setSubmitting(true);
    try {
      await onSubmit(values);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {fields.map((f) => (
          <div
            key={f.key}
            className={f.type === "textarea" || f.type === "repeat" ? "sm:col-span-2" : ""}
          >
            {/* El checkbox lleva su propia etiqueta al costado. */}
            {f.type !== "checkbox" && (
              <label className={`mb-1 block text-xs font-medium ${themeClasses.textSecondary}`}>
                {f.label}
                {f.required && <span className="ml-0.5 text-red-500">*</span>}
              </label>
            )}

            {f.type === "checkbox" ? (
              <label className="flex cursor-pointer items-center gap-2 py-2">
                <input
                  type="checkbox"
                  checked={values[f.key] === "true"}
                  onChange={(e) => set(f.key, String(e.target.checked))}
                  className="h-4 w-4 rounded border-gray-300 text-primary-500 focus:ring-primary-500"
                />
                <span className={`text-sm ${themeClasses.text}`}>{f.label}</span>
              </label>
            ) : f.type === "repeat" ? (
              <RepeaterField
                columns={f.repeatColumns ?? []}
                addRowLabel={f.addRowLabel ?? t("payroll.addRow")}
                isDarkMode={isDarkMode}
                themeClasses={themeClasses}
                value={values[f.key]}
                onChange={(json) => set(f.key, json)}
              />
            ) : f.type === "select" ? (
              <select
                value={values[f.key]}
                onChange={(e) => set(f.key, e.target.value)}
                className={inputBase}
              >
                <option value="">—</option>
                {f.options?.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            ) : f.type === "textarea" ? (
              <textarea
                rows={3}
                value={values[f.key]}
                placeholder={f.placeholder}
                onChange={(e) => set(f.key, e.target.value)}
                className={inputBase}
              />
            ) : (
              <input
                type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                value={values[f.key]}
                placeholder={f.placeholder}
                onChange={(e) => set(f.key, e.target.value)}
                className={inputBase}
              />
            )}

            {f.hint && (
              <p className={`mt-1 text-[11px] ${themeClasses.textSecondary}`}>{f.hint}</p>
            )}
            {errors[f.key] && (
              <p className="mt-1 text-xs text-red-500">{t("payroll.requiredField")}</p>
            )}
          </div>
        ))}
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button
          onClick={onCancel}
          className={`rounded-lg border px-4 py-2 text-sm ${themeClasses.border} ${themeClasses.buttonText} ${themeClasses.buttonHover}`}
        >
          {t("payroll.cancel")}
        </button>
        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="rounded-lg bg-primary-500 px-4 py-2 text-sm text-white transition-colors hover:bg-primary-400 disabled:opacity-60"
        >
          {submitting ? t("payroll.loading") : submitLabel}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Editor de líneas para campos `repeat`: campos configurables del tipo de
// actuación, reglas de consumo automático (AgroCloud#80) e ítems del check list (AgroCloud#17).
// Lee y escribe el valor como array JSON de objetos-fila.
// ---------------------------------------------------------------------------
type RepeaterRow = Record<string, string | number | boolean>;

interface RepeaterFieldProps {
  columns: RepeatColumn[];
  addRowLabel: string;
  isDarkMode: boolean;
  themeClasses: ThemeClasses;
  value: string;
  onChange: (json: string) => void;
}

function RepeaterField({
  columns,
  addRowLabel,
  isDarkMode,
  themeClasses,
  value,
  onChange,
}: RepeaterFieldProps) {
  const { t } = useTranslation("common");
  let rows: RepeaterRow[] = [];
  try {
    const parsed = JSON.parse(value || "[]");
    if (Array.isArray(parsed)) rows = parsed as RepeaterRow[];
  } catch {
    rows = [];
  }

  const cellBase = `w-full rounded-md border px-2 py-1.5 text-sm outline-none ${
    isDarkMode
      ? "bg-[#232323] border-dark-border text-white focus:border-primary-500"
      : "bg-white border-gray-300 text-gray-900 focus:border-primary-500"
  }`;

  const commit = (next: RepeaterRow[]) => onChange(JSON.stringify(next));

  const blankValue = (c: RepeatColumn): string | number | boolean => {
    if (c.type === "number") return 0;
    if (c.type === "checkbox") return false;
    if (c.type === "select") return c.options?.[0]?.value ?? "";
    return "";
  };

  const addRow = () => {
    const blank: RepeaterRow = {};
    columns.forEach((c) => (blank[c.key] = blankValue(c)));
    commit([...rows, blank]);
  };

  const updateCell = (i: number, key: string, raw: string | boolean, type?: string) => {
    const parsed =
      type === "number" ? Number(raw) || 0 : type === "checkbox" ? Boolean(raw) : raw;
    commit(rows.map((r, idx) => (idx === i ? { ...r, [key]: parsed } : r)));
  };

  const removeRow = (i: number) => commit(rows.filter((_, idx) => idx !== i));

  return (
    <div
      className={`rounded-lg border p-2 ${themeClasses.border} ${
        isDarkMode ? "bg-[#1c1c1c]" : "bg-gray-50"
      }`}
    >
      {rows.length === 0 ? (
        <p className={`px-1 py-2 text-xs ${themeClasses.textSecondary}`}>{t("payroll.noRows")}</p>
      ) : (
        <div className="space-y-2">
          {rows.map((row, i) => (
            <div key={i} className="flex flex-wrap items-end gap-2 sm:flex-nowrap">
              {columns.map((c) => (
                <div key={c.key} className="min-w-[7rem] flex-1">
                  <span className={`mb-0.5 block text-[10px] ${themeClasses.textSecondary}`}>
                    {c.label}
                  </span>
                  {c.type === "select" ? (
                    <select
                      value={String(row[c.key] ?? "")}
                      onChange={(e) => updateCell(i, c.key, e.target.value, "select")}
                      className={cellBase}
                    >
                      {c.options?.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  ) : c.type === "checkbox" ? (
                    <input
                      type="checkbox"
                      checked={Boolean(row[c.key])}
                      onChange={(e) => updateCell(i, c.key, e.target.checked, "checkbox")}
                      className="mt-2 h-4 w-4 rounded border-gray-300 text-primary-500 focus:ring-primary-500"
                    />
                  ) : (
                    <input
                      type={c.type === "number" ? "number" : "text"}
                      value={String(row[c.key] ?? "")}
                      placeholder={c.placeholder}
                      onChange={(e) => updateCell(i, c.key, e.target.value, c.type)}
                      className={cellBase}
                    />
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => removeRow(i)}
                title={t("payroll.delete")}
                className="mb-0.5 rounded-md p-1.5 text-red-500 hover:bg-red-500/10"
              >
                <i className="ri-delete-bin-line" />
              </button>
            </div>
          ))}
        </div>
      )}
      <button
        type="button"
        onClick={addRow}
        className="mt-2 flex items-center gap-1 rounded-md border border-dashed border-primary-500/50 px-3 py-1.5 text-xs text-primary-500 hover:bg-primary-500/10"
      >
        <i className="ri-add-line" />
        {addRowLabel}
      </button>
    </div>
  );
}
